import { isReservedFrontUrlSegment } from '@constants/reserved-front-urls';
import { CommonService } from '@modules/shared/services/common.service';
import { resolveCoverPageCreatorGsi4pk } from '@modules/whitelabel-cover-page/cover-page-upload.utils';
import {
  buildSubPageMenuOptionId,
  buildSubPagePath,
  getHeaderMenuFieldName,
  HEADER_ALWAYS_SELECTED_MENU_IDS,
  HEADER_LAYOUT_CONSTANTS,
  isAlwaysSelectedHeaderMenuId,
  isContentFeedMenuId,
  isPortfolioMenuOptionId,
  isSubPagePathFor
} from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.constants';
import {
  buildHeaderMenuMultiselectOptions,
  buildHeaderMenuOptionUrl,
  buildHeaderMenuProfileContext,
  buildPortfolioMenuOptionId,
  getHeaderMenuOptionLabel,
  HeaderMenuProfileApiData,
  HeaderMenuProfileContext
} from '@modules/profile/component/marketing-page-layout-container/header-layout/header-profile-menu-options.utils';

export interface HeaderMenuListItem {
  id: string;
  label: string;
  url: string;
  isPredefined: boolean;
  isLocked?: boolean;
}

export interface HeaderMenuLabelItem {
  id: string;
  url: string;
  label: string;
}

export function normalizeHeaderMenuUrl(url: string): string {
  return String(url ?? '').trim().replace(/\/+$/, '');
}

export function extractHeaderMenuLabelItemsFromData(
  data: Record<string, unknown>
): HeaderMenuLabelItem[] {
  const items: HeaderMenuLabelItem[] = [];
  for (let i = 1; i <= HEADER_LAYOUT_CONSTANTS.MENU_COUNT; i++) {
    const label = String(data[getHeaderMenuFieldName(i, 'label')] ?? '').trim();
    const url = String(data[getHeaderMenuFieldName(i, 'url')] ?? '').trim();
    if (!label && !url) {
      continue;
    }
    items.push({
      id: '',
      url,
      label
    });
  }
  return items;
}

export function withHeaderMenuLockFlags(
  items: HeaderMenuListItem[]
): HeaderMenuListItem[] {
  return items.map((item) => ({
    ...item,
    isLocked: isAlwaysSelectedHeaderMenuId(item.id)
  }));
}

function buildLockedHeaderMenuItem(
  optionId: string,
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData
): HeaderMenuListItem | null {
  const url = buildHeaderMenuOptionUrl(optionId, context);
  if (!url) {
    return null;
  }
  return {
    id: optionId,
    label: getHeaderMenuOptionLabel(optionId, apiData),
    url,
    isPredefined: true,
    isLocked: true
  };
}

function isLegacyContentFeedUrl(
  url: string,
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData
): boolean {
  const normalized = normalizeHeaderMenuUrl(url);
  if (!normalized || /^(https?:|mailto:|tel:|#)/i.test(normalized)) {
    return false;
  }
  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  const segments = path.split('/').filter(Boolean);
  if (segments.length !== 1) {
    return false;
  }
  const slug = segments[0];
  if (isReservedFrontUrlSegment(slug, [context.username])) {
    return false;
  }
  return !apiData.subPages?.some((subPage) =>
    isSubPagePathFor(path, context.username, subPage.url)
  );
}

function reclaimRenamedContentFeedItem(
  items: HeaderMenuListItem[],
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData
): HeaderMenuListItem[] {
  if (items.some((item) => isContentFeedMenuId(item.id))) {
    return items;
  }
  const feedUrl = buildHeaderMenuOptionUrl(
    HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS,
    context
  );
  if (!feedUrl) {
    return items;
  }
  const candidateIndex = items.findIndex(
    (item) =>
      !item.isPredefined && isLegacyContentFeedUrl(item.url, context, apiData)
  );
  if (candidateIndex < 0) {
    return items;
  }
  const candidate = items[candidateIndex];
  const next = [...items];
  next[candidateIndex] = {
    id: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS,
    label: candidate.label,
    url: feedUrl,
    isPredefined: true,
    isLocked: true
  };
  return next;
}

export function ensureLockedHeaderMenuItems(
  items: HeaderMenuListItem[],
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData = {}
): HeaderMenuListItem[] {
  const flagged = withHeaderMenuLockFlags(
    reclaimRenamedContentFeedItem(items, context, apiData)
  );
  const missing = HEADER_ALWAYS_SELECTED_MENU_IDS.filter(
    (optionId) => !flagged.some((item) => item.id === optionId)
  )
    .map((optionId) => buildLockedHeaderMenuItem(optionId, context, apiData))
    .filter((item): item is HeaderMenuListItem => !!item);
  return missing.length ? [...missing, ...flagged] : flagged;
}

function resolveCreatorUsername(commonService: CommonService): string {
  const fromWl =
    commonService.getWhiteLabelProperty('user.user_name') ||
    commonService.getWhiteLabelConfiguration()?.user?.user_name;
  if (typeof fromWl === 'string' && fromWl.trim()) {
    return fromWl.trim();
  }
  const loggedIn = commonService.getLoggedInUser()?.user_name;
  return typeof loggedIn === 'string' ? loggedIn.trim() : '';
}

export function buildHeaderMenuProfileContextFromCommonService(
  commonService: CommonService
): HeaderMenuProfileContext {
  return buildHeaderMenuProfileContext(
    resolveCreatorUsername(commonService),
    resolveCoverPageCreatorGsi4pk(commonService) ?? ''
  );
}

function createCustomMenuId(): string {
  return `custom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createHeaderCustomMenuItem(
  label = '',
  url = ''
): HeaderMenuListItem {
  return {
    id: createCustomMenuId(),
    label,
    url,
    isPredefined: false
  };
}

export function buildPredefinedMenuOptions(
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData = {}
): HeaderMenuListItem[] {
  return buildHeaderMenuMultiselectOptions(context, apiData);
}

function resolveMenuItemLabelAndUrl(
  item: HeaderMenuListItem,
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData
): { label: string; url: string } {
  if (!item.isPredefined) {
    return {
      label: item.label.trim(),
      url: item.url.trim()
    };
  }
  return {
    label: item.label.trim() || getHeaderMenuOptionLabel(item.id, apiData),
    url: buildHeaderMenuOptionUrl(item.id, context) || item.url.trim()
  };
}

function identifyMenuItemFromSlot(
  label: string,
  url: string,
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData,
  seenIds: Set<string>
): HeaderMenuListItem | null {
  const availableOptions = buildHeaderMenuMultiselectOptions(context, apiData);
  for (const option of availableOptions) {
    if (seenIds.has(option.id)) {
      continue;
    }
    const optionUrl = buildHeaderMenuOptionUrl(option.id, context);
    if (
      (optionUrl && url === optionUrl) ||
      label.toLowerCase() === option.label.toLowerCase()
    ) {
      seenIds.add(option.id);
      return {
        id: option.id,
        label: label || option.label,
        url: optionUrl || url,
        isPredefined: true
      };
    }
  }

  const portfolioMatch = url.match(
    /\/dashboard\/portfolio\/[^/]+\/banner-view\/([^/?#]+)/
  );
  if (portfolioMatch?.[1]) {
    const optionId = buildPortfolioMenuOptionId(portfolioMatch[1]);
    if (!seenIds.has(optionId)) {
      seenIds.add(optionId);
      return {
        id: optionId,
        label: label || getHeaderMenuOptionLabel(optionId, apiData),
        url,
        isPredefined: true
      };
    }
  }

  const staticIds = Object.values(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS);
  for (const optionId of staticIds) {
    if (seenIds.has(optionId)) {
      continue;
    }
    const optionUrl = buildHeaderMenuOptionUrl(optionId, context);
    if ((optionUrl && url === optionUrl) || matchesStaticMenuLabel(optionId, label)) {
      seenIds.add(optionId);
      return {
        id: optionId,
        label: label || getHeaderMenuOptionLabel(optionId, apiData),
        url: optionUrl || url,
        isPredefined: true
      };
    }
  }

  if (isPortfolioMenuOptionId(label)) {
    return null;
  }

  const subPageMatch = apiData.subPages?.find(
    (sp) =>
      isSubPagePathFor(url, context.username, sp.url) ||
      label.toLowerCase() === sp.name.toLowerCase()
  );
  if (subPageMatch && !seenIds.has(buildSubPageMenuOptionId(subPageMatch.url))) {
    const optionId = buildSubPageMenuOptionId(subPageMatch.url);
    seenIds.add(optionId);
    return {
      id: optionId,
      label: label || subPageMatch.name,
      url: buildSubPagePath(context.username, subPageMatch.url) || url,
      isPredefined: true
    };
  }

  return null;
}

function matchesStaticMenuLabel(optionId: string, label: string): boolean {
  const labels = HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS;
  const normalized = label.trim().toLowerCase();
  switch (optionId) {
    case HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS:
      return normalized === labels.FEEDS.toLowerCase();
    // case HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.LINK_IN_BIO:
    //   return normalized === labels.LINK_IN_BIO.toLowerCase();
    case HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.STORE:
      return normalized === labels.STORE.toLowerCase();
    default:
      return false;
  }
}

export function hasPersistedHeaderMenus(data: Record<string, unknown>): boolean {
  for (let i = 1; i <= HEADER_LAYOUT_CONSTANTS.MENU_COUNT; i++) {
    if (
      String(data[getHeaderMenuFieldName(i, 'label')] ?? '').trim() ||
      String(data[getHeaderMenuFieldName(i, 'url')] ?? '').trim()
    ) {
      return true;
    }
  }
  return false;
}

export function parseHeaderMenusFromData(
  data: Record<string, unknown>,
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData = {}
): HeaderMenuListItem[] {
  const items: HeaderMenuListItem[] = [];
  const seenIds = new Set<string>();

  for (let i = 1; i <= HEADER_LAYOUT_CONSTANTS.MENU_COUNT; i++) {
    const label = String(data[getHeaderMenuFieldName(i, 'label')] ?? '').trim();
    const url = String(data[getHeaderMenuFieldName(i, 'url')] ?? '').trim();
    if (!label && !url) {
      continue;
    }
    const predefined = identifyMenuItemFromSlot(
      label,
      url,
      context,
      apiData,
      seenIds
    );
    if (predefined) {
      items.push(predefined);
      continue;
    }
    items.push({
      id: createCustomMenuId(),
      label,
      url,
      isPredefined: false
    });
  }

  return items;
}

export function flattenHeaderMenusToData(
  items: HeaderMenuListItem[],
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData = {}
): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 1; i <= HEADER_LAYOUT_CONSTANTS.MENU_COUNT; i++) {
    out[getHeaderMenuFieldName(i, 'label')] = '';
    out[getHeaderMenuFieldName(i, 'url')] = '';
  }

  const menuItems = items
    .map((item) => resolveMenuItemLabelAndUrl(item, context, apiData))
    .filter((item) => item.label || item.url);

  menuItems.slice(0, HEADER_LAYOUT_CONSTANTS.MENU_COUNT).forEach((item, index) => {
    const slot = index + 1;
    out[getHeaderMenuFieldName(slot, 'label')] = item.label;
    out[getHeaderMenuFieldName(slot, 'url')] = item.url;
  });

  return out;
}

export function getDefaultHeaderMenuItems(
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData = {}
): HeaderMenuListItem[] {
  return ensureLockedHeaderMenuItems([], context, apiData);
}

export function mergeHeaderMenuMultiselectOptions(
  predefinedOptions: HeaderMenuListItem[],
  selectedItems: HeaderMenuListItem[]
): HeaderMenuListItem[] {
  const optionMap = new Map(predefinedOptions.map((item) => [item.id, item]));
  selectedItems.forEach((item) => {
    const existing = optionMap.get(item.id);
    if (!existing) {
      optionMap.set(item.id, { ...item });
      return;
    }
    optionMap.set(item.id, {
      ...existing,
      ...item,
      label: String(item.label ?? '').trim() || existing.label,
      url: String(item.url ?? '').trim() || existing.url,
      isPredefined: existing.isPredefined,
      isLocked: existing.isLocked ?? item.isLocked
    });
  });
  return withHeaderMenuLockFlags(Array.from(optionMap.values()));
}

export function syncSelectedMenuItemsWithOptions(
  selectedItems: HeaderMenuListItem[],
  options: HeaderMenuListItem[]
): HeaderMenuListItem[] {
  const optionMap = new Map(options.map((item) => [item.id, item]));
  return withHeaderMenuLockFlags(
    selectedItems.map((item) => {
      const match = optionMap.get(item.id);
      if (!match) {
        return { ...item };
      }
      if (item.isPredefined) {
        return {
          ...match,
          label: String(item.label ?? '').trim() || match.label,
          url: match.url || item.url
        };
      }
      return {
        ...match,
        ...item,
        label: String(item.label ?? match.label ?? '').trim() || match.label,
        url: String(item.url ?? match.url ?? '').trim() || match.url
      };
    })
  );
}

export function getDefaultHeaderSectionData(
  commonService: CommonService,
  apiData: HeaderMenuProfileApiData = {}
): Record<string, string> {
  const context = buildHeaderMenuProfileContextFromCommonService(commonService);
  const menuData = flattenHeaderMenusToData(
    getDefaultHeaderMenuItems(context, apiData),
    context,
    apiData
  );
  return {
    ...menuData,
    [HEADER_LAYOUT_CONSTANTS.DATA_KEY_CTA_TEXT]: '',
    [HEADER_LAYOUT_CONSTANTS.DATA_KEY_CTA_LINK]: '',
    [HEADER_LAYOUT_CONSTANTS.DATA_KEY_CTA_TEXT_2]: '',
    [HEADER_LAYOUT_CONSTANTS.DATA_KEY_CTA_LINK_2]: ''
  };
}
